import { html, nothing } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import store from "../../store/store";
import { StoreState } from "../../store/store.interface";
import { connect } from 'pwa-helpers';
import { areProductsUnavailable, getCartError, getCartStatus, isCartEmpty, isK12CustomStoreAdmin } from "../cart/shop-cart/store/shop-cart.selectors";
import env from "../../common/environment"
import { isFailureUser } from "../../features/user-profile/user-profile.selectors";
import "./national-checkout-steps/national-checkout-steps";
import "./k12-checkout-steps/k12-storefront-checkout-steps";
import "./tunnel-checkout-steps/tunnel-checkout-steps";
import { isTunnelPurchaseFlow } from "../../common/utils/util";
import { BaseComponent } from "../../features/base-component/base-component";
import emit from "../../common/events";
import { ActionStatus } from "../../common/constants/action-status";
import { handleApiError } from "../../common/utils/api-utils";
import { booleanConverter } from "../../common/converters";
import { getShopCart } from "../cart/shop-cart/thunks/get-shop-cart.thunk";
import { getCheckoutLoader } from "../../common/utils/loader-util";
import { getOrderStatus, getOrderStatusFailureCount, getOrderStatusReprocess } from "./shop-checkout";
import { smallSpinner } from "../../common/constants/api-constants";
import { ORDER_STATUS_PENDING_MESSAGE, orderStatusFailureAlert } from "../order-placement/utils/order-placement.util";
import { Modal } from "../modal/modal";
import { OrderStatus } from "./shop-checkout.interface";

/**
 * An element to determine checkout flow, load checkout steps based on the flow and complete the checkout process.
 */
@customElement('mhecomm-checkout-steps')
export class CheckoutSteps extends connect(store)(BaseComponent) {

  /**
   * is guest checkout to support for non authenticated users
   */
  @property({converter: booleanConverter})
  isGuestCheckout = false;

  /**
   * Store code related to the checkout process. If not set it will be the national process.
   */
  @property()
  store?: string;

  /**
   * Checkout steps to render
   */
  @state()
  private checkoutStepsTemplate = html`${getCheckoutLoader()}`;

  /**
   * Flag to indicate that order status has failed.
   */
  @state()
  protected isOrderStatusFailure = false;

  @state()
  private modal?: Modal;

  /**
   * When checkout steps is first updated, load cart and determine checkout steps template to load.
   * @param _changedProperties 
   */
  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await store.dispatch(getShopCart({ store: this.store }));
    this.determineCheckoutStepsTemplate();
  }

  determineCheckoutStepsTemplate(){
    if(isTunnelPurchaseFlow()) {
      this.checkoutStepsTemplate = this.renderTunnelPurchaseCheckoutSteps();
    } else if(isK12CustomStoreAdmin(store.getState() as StoreState)) {
      this.checkoutStepsTemplate = this.renderK12CheckoutSteps();
    } else {
      this.checkoutStepsTemplate = this.renderNationalCheckoutSteps();
    }
  }

  /**
   * If user is not logged-in, redirect to login page.
   * If cart is empty redirect to home page.
   * If some products are unavailable redirect to cart page.
   * @param state 
   * @returns 
   */
  stateChanged(state: StoreState) {
    const userIsLoggedOut = isFailureUser(state);
    if (!window.Mhecomm_Editor) {
      if (userIsLoggedOut && !this.isGuestCheckout) {
        emit({
          element: this,
          name: 'checkout-steps-redirect',
          dataLayer: true,
          detail: {
            reason: 'logged-out-user'
          }
        });
        sessionStorage.clear();
        window.location.href = env().loginUrl;
      }

      if(getCartStatus(state) === ActionStatus.succeeded){
        const cartIsEmpty = isCartEmpty(state);
        const someProductsAreUnavailable = areProductsUnavailable(state);

        if (cartIsEmpty) {
          emit({
            element: this,
            name: 'checkout-steps-redirect',
            dataLayer: true,
            detail: {
              reason: 'empty-cart'
            }
          });
          window.location.href = '/';
        }
        
        if (someProductsAreUnavailable) {
          emit({
            element: this,
            name: 'checkout-steps-redirect',
            dataLayer: true,
            detail: {
              reason: 'unavailable-cart'
            }
          });
          window.location.href = this.store ? env().customStoreCartUrl.replace(":storeCode", this.store) : env().cartUrl;
        }
      }
    }

    if (getCartStatus(state) === ActionStatus.failed){
      handleApiError(getCartError(state));
      return;
    }

    if (getOrderStatusReprocess(state) && getOrderStatusFailureCount(state) === 0) {
      this.renderOrderReprocessModal();
    } else if (getOrderStatusFailureCount(state) > 2) {
      this.modal?.close();
      this.isOrderStatusFailure = true;
    }

    if (getOrderStatus(state) === OrderStatus.FAILURE) {
      this.isOrderStatusFailure = true;
    }
  }

  renderComp() {
    return html`
      ${this.renderOrderFailureAlert()}
      ${this.checkoutStepsTemplate}
    `;
  }

  /**
   * Render order failure alert, when order status API fails multiple times (more than 2 times)
   */
  renderOrderFailureAlert() {
    return (this.isOrderStatusFailure)
      ? html `${orderStatusFailureAlert('order-status-failure-alert')}`
      : nothing;
  }

  renderTunnelPurchaseCheckoutSteps(){
    return html`<mhecomm-tunnel-checkout-steps .store="${this.store}"></mhecomm-tunnel-checkout-steps>`;
  }

  renderK12CheckoutSteps(){
    return html`<mhecomm-k12-checkout-steps .store="${this.store}"></mhecomm-k12-checkout-steps>`;
  }

  renderNationalCheckoutSteps(){
    return html`<mhecomm-national-checkout-steps .store="${this.store}"></mhecomm-national-checkout-steps>`;
  }

  renderOrderReprocessModal() {
    const content = html`
      <div class="d-flex gap-2">
        <span>${smallSpinner()}</span>
        <p class="mb-0">
          ${ORDER_STATUS_PENDING_MESSAGE}
        </p>
      </div>
    `
    this.modal = new Modal({
      modalContext: {componentRef: 'orderStatusReprocess'},
      header: '',
      body: {template: content},
      disableHeader: true,
      disableFooter: true,
      isModalCentered: true,
      enableBackgroundBlur: true
    });
    this.modal.open();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-checkout-steps': CheckoutSteps;
  }
}
